/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { BarChart3, CalendarRange, FileText, List, LineChart, LogIn, Network, Settings2, type LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import configuration */
import { APP_ROUTES } from '@/config/appRoutes';

interface DemoConfig {
    description: string;
    icon: LucideIcon;
    label: string;
    route: string;
}

const DEMOS: DemoConfig[] = [
    {
        description: 'Check, renew, or clear the current API token.',
        icon: LogIn,
        label: 'Login Demo',
        route: APP_ROUTES.logindemo,
    },
    {
        description: 'Read, edit, and save JSON configuration data for a customer and system.',
        icon: Settings2,
        label: 'Configuration',
        route: APP_ROUTES.configuration,
    },
    {
        description: 'Request and inspect time series data.',
        icon: BarChart3,
        label: 'Time Series',
        route: APP_ROUTES.timeseries,
    },
    {
        description: 'Request the latest value for one or more sensors.',
        icon: List,
        label: 'Current Value',
        route: APP_ROUTES.currentvalue,
    },
    {
        description: 'Request interval differences for one or more sensors.',
        icon: CalendarRange,
        label: 'Interval Values',
        route: APP_ROUTES.intervalvalues,
    },
    {
        description: 'Request and render interactive Plotly chart data.',
        icon: LineChart,
        label: 'Plotly',
        route: APP_ROUTES.plotly,
    },
    {
        description: 'Request stored chart definitions and render them as interactive Plotly charts.',
        icon: LineChart,
        label: 'Chart Definitions',
        route: APP_ROUTES.chartdefinitions,
    },
    {
        description: 'Load summarized energy statistics for every available supply type of a system.',
        icon: BarChart3,
        label: 'Building statistics',
        route: APP_ROUTES.buildingstatistics,
    },
    {
        description: 'Read all available customer and system configurations without editing them.',
        icon: Settings2,
        label: 'All configurations',
        route: APP_ROUTES.allconfigurations,
    },
    {
        description: 'Enter Markdown text and render it with the shared StaticMarkdown component.',
        icon: FileText,
        label: 'Markdown',
        route: APP_ROUTES.markdown,
    },
    {
        description: 'Enter a Mermaid definition and render it with the shared StaticMermaidDiagram component.',
        icon: Network,
        label: 'Mermaid',
        route: APP_ROUTES.mermaid,
    },
    {
        description: 'Enter Plotly JSON and render it with the shared StaticStoredPlotlyPlot component.',
        icon: LineChart,
        label: 'Plotly',
        route: APP_ROUTES.plotlyjson,
    },
];

function DemoTab() {
    const { t } = useI18n();

    return (
        <div className="flex flex-col gap-6" aria-label={t('Demo')}>
            <section className="flex flex-col gap-2">
                <h2>{t('Demo')}</h2>
                <p>{t('Select a demo to explore the available examples and reference pages:')}</p>
            </section>
            <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {DEMOS.map((demo) => {
                    const Icon = demo.icon;

                    return (
                        <NavLink
                            key={demo.route}
                            to={demo.route}
                            className="dashboard-panel interactive-surface !min-h-44 no-underline transition-[background,transform] duration-150"
                        >
                            <div className="flex h-full w-full flex-col items-start justify-center gap-3 px-6 text-left">
                                <div className="flex w-full items-start justify-between gap-3">
                                    <div className="flex items-start gap-3">
                                        <Icon className="mt-0.5 h-5 w-5 shrink-0 text-base-content/75" aria-hidden="true" />
                                        <span className="text-xl font-semibold">{t(demo.label)}</span>
                                    </div>
                                    <span className="badge badge-primary shrink-0">{t('Demo')}</span>
                                </div>
                                <span className="text-sm text-base-content/70">
                                    {t(demo.description)}
                                </span>
                            </div>
                        </NavLink>
                    );
                })}
            </section>
        </div>
    );
}

export default DemoTab;
